import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { api } from '@/api/client'
import type { ScoreSheet } from '@/api/types'
import { SidePicker, type Side } from '@/components/SidePicker'
import { whyNotReady } from '@/lib/game'
import { tokens } from '@/theme'

/**
 * Today, where the phone is — not `toISOString()`, which is UTC and would
 * hand back tomorrow's date to anybody playing a US evening. That is the same
 * bug the server-side default had, and it is worth not writing twice.
 */
function localToday(): string {
  const now = new Date()
  const month = `${now.getMonth() + 1}`.padStart(2, '0')
  const day = `${now.getDate()}`.padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

/** Now, on the phone's own clock, as the `HH:MM` a time input wants. */
function localNow(): string {
  const now = new Date()
  return `${`${now.getHours()}`.padStart(2, '0')}:${`${now.getMinutes()}`.padStart(2, '0')}`
}

/** `19:20:00` from the API, or `19:20` from the input, as `7:20 PM`. */
function clock(hhmm: string): string {
  const [h, m] = hhmm.split(':')
  const at = new Date()
  at.setHours(Number(h), Number(m), 0, 0)
  return AT.format(at)
}

//: Games the list opens with. Matches the server's own default.
const SHEET_PAGE = 20

const AT = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
})

const NIGHT = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
})

/**
 * Put a score in. No account, ever.
 *
 * The mirror of `Invite` and `Pay`, and public for the same reason: the token
 * IS the credential. What is different is that this one is not per person —
 * it is pasted into a group chat and forwarded by people we will never
 * identify — so its power is cut to the bone. It can type a result and
 * nothing else: it cannot add a player, rename anything, see an address, or
 * read the table. The standings are behind sign-in with the roster.
 *
 * Built for a phone held between points, and rebuilt once. The first version
 * was a cloud of chips you tapped once for one side and twice for the other,
 * over two boxes labelled "Side A" and "Side B" that took the SCORE — which
 * read as "type the names here", because a field labelled with a side is a
 * field for that side. Two problems with one cause: nothing said which
 * control did what.
 *
 * So: every player on their own row with two buttons that say A and B, and
 * the score on the line that already names the side it belongs to. Nothing
 * to learn and nothing to read twice.
 */
export function Score() {
  const { token = '' } = useParams()
  const queryClient = useQueryClient()

  const [sides, setSides] = useState<Record<string, Side>>({})
  const [playedOn, setPlayedOn] = useState(localToday())
  const [playedAt, setPlayedAt] = useState(localNow())
  const [note, setNote] = useState('')
  //: A night is six or eight games for most groups and thirty for a busy one.
  //: The whole list courtside is a scroll past the thing you came to press,
  //: so it opens at the most recent few and says how many more there are.
  const [limit, setLimit] = useState(SHEET_PAGE)
  const [scoreA, setScoreA] = useState('')
  const [scoreB, setScoreB] = useState('')
  const [error, setError] = useState('')

  // Keyed on the night as well as the token: changing the date asks the
  // server what is already in for THAT night. A list that does not match the
  // date above it is worse than no list.
  // Keyed on the limit, not on the date. The date says what a NEW score is
  // filed under; the list below is simply the most recent games, whichever
  // nights they fall on.
  const sheet = useQuery({
    queryKey: ['score', token, limit],
    queryFn: () => api.get<ScoreSheet>(`/api/s/${token}?limit=${limit}`),
    retry: false,
  })

  const picked = (side: Side) =>
    Object.entries(sides)
      .filter(([, s]) => s === side)
      .map(([id]) => id)

  const save = useMutation({
    mutationFn: () =>
      api.post<ScoreSheet>(`/api/s/${token}`, {
        side_a: picked('a'),
        side_b: picked('b'),
        score_a: Number(scoreA),
        score_b: Number(scoreB),
        played_on: playedOn,
        played_at: playedAt || null,
        note,
      }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['score', token, limit], fresh)
      // Everything clears. The next game has different partners, and a form
      // that keeps the last four selected is a form that quietly records the
      // same pairing twice.
      setSides({})
      setScoreA('')
      setScoreB('')
      setNote('')
      // The clock moves on to the next game; the night does not.
      setPlayedAt(localNow())
      setError('')
    },
    onError: (e: Error) => setError(e.message),
  })

  // Memoised, not `?? []` inline: a fresh empty array every render would
  // make both memos below recompute every render, which is the whole reason
  // they exist.
  const players = useMemo(() => sheet.data?.players ?? [], [sheet.data])

  const names = useMemo(
    () => Object.fromEntries(players.map((p) => [p.person_id, p.name])),
    [players],
  )

  const put = (personId: string, side: Side | null) =>
    setSides((was) => {
      const next = { ...was }
      if (side === null) delete next[personId]
      else next[personId] = side
      return next
    })

  if (sheet.isLoading)
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2">Loading…</Typography>
      </Box>
    )

  if (sheet.isError || !sheet.data)
    return (
      <Box sx={{ p: 3, maxWidth: 480, mx: 'auto' }}>
        <Typography variant="h1">Nothing here</Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          This scoring link is not live. It may have been replaced — ask
          whoever runs the group for the current one.
        </Typography>
      </Box>
    )

  const data = sheet.data
  const sideA = picked('a')
  const sideB = picked('b')
  // The reason, not just the fact. A grey button that will not say why is the
  // same failure as one that does nothing.
  const blocked = whyNotReady(sideA, sideB, scoreA, scoreB)

  const roll = (ids: string[]) =>
    ids.map((id) => names[id] ?? 'Someone').join(' & ')

  return (
    <Box sx={{ p: 2, maxWidth: 560, mx: 'auto' }}>
      <Typography variant="h1">{data.label}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Put in a score.
      </Typography>

      <Paper sx={{ p: 2, mt: 2 }}>
        {/* The night, first, because everything under it is about that night.
            Defaults to today and is usually left alone — it is here for the
            Wednesday morning when somebody types up Tuesday. */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField
            label="Night played"
            type="date"
            value={playedOn}
            onChange={(e) => setPlayedOn(e.target.value || localToday())}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ flex: 1, minWidth: 0 }}
          />
          {/* Defaults to now and is almost never touched. It is here so a
              night's games come back in the order they were played rather
              than the order somebody got round to typing them. */}
          <TextField
            label="Time"
            type="time"
            value={playedAt}
            onChange={(e) => setPlayedAt(e.target.value)}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 130, flexShrink: 0 }}
          />
        </Stack>
        <SidePicker players={players} sides={sides} onPut={put} />
      </Paper>

      {/* The score sits on the line that already names the side, so neither
          box has to be labelled with a side of its own — which is what made
          the old pair read as somewhere to type the names. */}
      <Paper sx={{ p: 2, mt: 2 }}>
        {(['a', 'b'] as const).map((side) => {
          const ids = side === 'a' ? sideA : sideB
          return (
            <Stack
              key={side}
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ py: 0.75 }}
            >
              <Typography variant="caption" sx={{ width: 44, flexShrink: 0 }}>
                Side {side.toUpperCase()}
              </Typography>
              {/* Wraps rather than squeezing the score box. Four names and
                  an ampersand do not fit beside a number on a 360px phone,
                  and the number is the part that must never move. */}
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '0.9375rem',
                  lineHeight: 1.3,
                  color: ids.length ? 'text.primary' : 'text.secondary',
                }}
              >
                {ids.length ? roll(ids) : 'nobody yet'}
              </Typography>
              <TextField
                value={side === 'a' ? scoreA : scoreB}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '')
                  if (side === 'a') setScoreA(digits)
                  else setScoreB(digits)
                }}
                // A numeric keypad, not a keyboard. The whole page is one
                // hand on a phone.
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  'aria-label': `Side ${side.toUpperCase()} score`,
                  style: { textAlign: 'center' },
                }}
                placeholder="0"
                sx={{
                  width: 88,
                  flexShrink: 0,
                  '& input': { fontSize: '1.25rem', fontWeight: 680, py: 1.1 },
                }}
              />
            </Stack>
          )
        })}

        <TextField
          label="Anything worth saying (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 200))}
          placeholder="Went to deuce twice"
          fullWidth
          size="small"
          sx={{ mt: 1.5 }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.8125rem' }}>
            {error}
          </Alert>
        )}

        {blocked && !error && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mt: 1.5 }}
          >
            {blocked}
          </Typography>
        )}

        <Button
          variant="contained"
          size="large"
          fullWidth
          sx={{ mt: 1, minHeight: 48 }}
          disabled={Boolean(blocked) || save.isPending}
          onClick={() => save.mutate()}
        >
          Save it
        </Button>
      </Paper>

      {/* Today only. This page is used courtside, where the useful question
          is whether somebody already put that one in — not how the season is
          going. The season is behind sign-in. */}
      {data.recent.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
            Recently put in
          </Typography>
          <Stack spacing={0.5}>
            {data.recent.map((game) => (
              <Box
                key={game.id}
                sx={{
                  py: 0.75,
                  borderBottom: `1px solid ${tokens.line}`,
                  '&:last-of-type': { borderBottom: 0 },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {game.side_a.join(' & ')}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${game.score_a}–${game.score_b}`}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  />
                  <Typography variant="body2" sx={{ flex: 1, textAlign: 'right' }}>
                    {game.side_b.join(' & ')}
                  </Typography>
                </Stack>
                {/* The date on every row, because the list crosses nights
                    now. Without it two games a week apart sit against each
                    other saying nothing about which was which. */}
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
                >
                  {NIGHT.format(new Date(`${game.played_on}T00:00:00`))}
                  {game.played_at && ` · ${clock(game.played_at)}`}
                  {game.note && ` · ${game.note}`}
                </Typography>
              </Box>
            ))}
          </Stack>
          {data.total > data.recent.length && (
            <Button
              size="small"
              onClick={() => setLimit((n) => n + SHEET_PAGE)}
              sx={{ mt: 1 }}
            >
              {`Show ${Math.min(SHEET_PAGE, data.total - data.recent.length)} more`}
            </Button>
          )}
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mt: 1 }}
          >
            Typed one wrong? Whoever runs the group can take it off.
          </Typography>
        </Paper>
      )}
    </Box>
  )
}
